"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronDown, ArrowLeft, X } from "lucide-react"
import { useRouter } from "next/navigation"
import { useMarketplaceStore } from "@/store/marketplace-store"
import { useUserStore } from "@/store/user-store";
import proxiedFetch from "@/lib/proxied-fetch"
import { API, creds } from "@/constants/api"
import { storageService } from "@/services/storage.service"
import { useAuth } from "@/hooks/use-auth"





// Cache key for storing marketplace vendor login data
const VENDOR_LOGIN_CACHE_KEY = "marketplace-vendor-login-data";

import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { CustomSelect } from "@/components/ui/custom-select"
import { countryList } from "@/components/ui/country-flags"
import { create } from "zustand"

// Zustand store for login form state
interface LoginFormState {
  phoneNumber: string
  setPhoneNumber: (number: string) => void
  countryCode: string
  setCountryCode: (code: string) => void
  isOtpSent: boolean
  setIsOtpSent: (sent: boolean) => void
  otp: string
  setOtp: (otp: string) => void
  // Terms and conditions acceptance
  termsAccepted: boolean
  setTermsAccepted: (accepted: boolean) => void
  // OTP response data
  refId: string | null
  setRefId: (refId: string | null) => void
  c_hash: string | null
  setC_hash: (hash: string | null) => void
  userType: string | null
  setUserType: (type: string | null) => void
  loyalCustomer: string | null
  setLoyalCustomer: (status: string | null) => void
  passwordSetFlag: boolean | null
  setPasswordSetFlag: (flag: boolean | null) => void
  phoneNumberEnrolled: boolean | null
  setPhoneNumberEnrolled: (enrolled: boolean | null) => void
  // Code verifier for PKCE
  codeVerifier: string | null
  setCodeVerifier: (verifier: string | null) => void
  codeChallenge: string | null
  setCodeChallenge: (challenge: string | null) => void
}

const useLoginFormStore = create<LoginFormState>((set) => ({
  phoneNumber: "",
  setPhoneNumber: (number) => set({ phoneNumber: number }),
  countryCode: "+91",
  setCountryCode: (code) => set({ countryCode: code }),
  isOtpSent: false,
  setIsOtpSent: (sent) => set({ isOtpSent: sent }),
  otp: "",
  setOtp: (otp) => set({ otp }),
  // Terms and conditions acceptance
  termsAccepted: false,
  setTermsAccepted: (accepted) => set({ termsAccepted: accepted }),
  // OTP response data with initial null values
  refId: null,
  setRefId: (refId) => set({ refId }),
  c_hash: null,
  setC_hash: (hash) => set({ c_hash: hash }),
  userType: null,
  setUserType: (type) => set({ userType: type }),
  loyalCustomer: null,
  setLoyalCustomer: (status) => set({ loyalCustomer: status }),
  passwordSetFlag: null,
  setPasswordSetFlag: (flag) => set({ passwordSetFlag: flag }),
  phoneNumberEnrolled: null,
  setPhoneNumberEnrolled: (enrolled) => set({ phoneNumberEnrolled: enrolled }),
  // Code verifier for PKCE
  codeVerifier: null,
  setCodeVerifier: (verifier) => set({ codeVerifier: verifier }),
  codeChallenge: null,
  setCodeChallenge: (challenge) => set({ codeChallenge: challenge })
}))

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Base64 URL encode a byte array
 * @param array - The byte array to encode
 * @returns A URL-safe Base64 encoded string
 */
const base64UrlEncode = (array: Uint8Array): string => {
  const base64 = btoa(String.fromCharCode.apply(null, Array.from(array)));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

/**
 * Generate a cryptographically secure code verifier for PKCE
 * @returns A random string for code verifier
 */
const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return base64UrlEncode(array);
};

/**
 * Generate the code challenge from the code verifier
 * @param codeVerifier - The code verifier string
 * @returns A Promise that resolves to the code challenge
 */
const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hash = await window.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(hash));
};

/**
 * Flattens a nested object into a single-level object with dot notation keys
 * @param obj - The object to flatten
 * @param prefix - Key prefix for nested properties
 * @returns A flattened object
 */
const flattenPayload = (obj: Record<string, any>, prefix = ''): Record<string, string> => {
  return Object.keys(obj).reduce((acc: Record<string, string>, key: string) => {
    const pre = prefix.length ? `${prefix}.` : '';
    
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      Object.assign(acc, flattenPayload(obj[key], pre + key));
    } else {
      acc[pre + key] = obj[key];
    }
    
    return acc;
  }, {});
};

/**
 * Generate a cryptographically secure c_hash for API requests
 * that matches the server's validation logic
 * 
 * @param payload - Request payload object
 * @returns A Promise that resolves to a string representing the c_hash
 */
const generateCHash = async (payload: Record<string, any>): Promise<string> => {
  try {
    // Get marketplace_user_id from the payload
    const marketplace_user_id = payload.marketplace_user_id;
    if (!marketplace_user_id) {
      throw new Error("marketplace_user_id is required for c_hash generation");
    }
    
    const secret = API.SECRET;
    // Generate timestamp
    const timestamp = Date.now().toString();
    
    // Create a salt using timestamp:marketplace_user_id to match server expectations
    const salt = `${timestamp}:${marketplace_user_id}`;
    
    console.log(`[generateCHash] Creating salt: ${salt} with secret: ${secret.substring(0, 3)}***`);
    
    // Sign the salt using HMAC-SHA256 with SECRET_KEY
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secret);
    const saltData = encoder.encode(salt);
    
    // Create the HMAC-SHA256 key
    const key = await crypto.subtle.importKey(
      'raw', 
      keyData, 
      { name: 'HMAC', hash: 'SHA-256' }, 
      false, 
      ['sign']
    );
    
    // Sign the salt
    const signature = await crypto.subtle.sign('HMAC', key, saltData);
    
    // Encode the signature in Base64
    const signatureArray = Array.from(new Uint8Array(signature));
    const base64Signature = btoa(String.fromCharCode(...signatureArray));
    
    // Format as {hash}:{timestamp}
    return `${base64Signature}:${timestamp}`;
  } catch (error) {
    console.error("Error generating c_hash:", error);
    // Fallback to a simpler format if an error occurs
    const timestamp = Date.now();
    return `hash_fallback_${timestamp}:${timestamp}`;
  }
}

/**
 * Function to resend OTP
 * @param marketplace_user_id - Marketplace user ID
 * @param refId - Reference ID from previous OTP request
 * @returns Promise with the API response
 */
const resendOtp = async (
  marketplace_user_id: number,
  refId: string,
  _c_hash: string, // Not used, kept for compatibility
  client_id: string,
  client_secret: string
) => {
  // Define the request body type
  interface ResendOtpRequestBody {
    marketplace_user_id: number;
    refId: string;
    c_hash?: string;
  }
  
  // Prepare request body according to API documentation
  const requestBody: ResendOtpRequestBody = {
    marketplace_user_id,
    refId
  }
  
  // Generate a new c_hash for this request
  requestBody.c_hash = await generateCHash(requestBody);
  
  console.log("[ResendOtp] Request payload:", requestBody)
  console.log("[ResendOtp] API endpoint:", API.ENDPOINTS.RESEND_OTP);
  
  // Make API call using proxiedFetch with just the endpoint path
  const response = await proxiedFetch(API.ENDPOINTS.RESEND_OTP, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'client_id': client_id,
      'client_secret': client_secret
    },
    body: JSON.stringify(requestBody)
  })
  
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`)
  }
  
  return await response.json()
}

/**
 * Function to call the check-phone API
 * @param marketplace_user_id - Marketplace user ID from IndexedDB
 * @param countryCode - Country code from input
 * @param phone - Phone number from input
 * @returns Promise with the API response
 */
const checkPhone = async (
  marketplace_user_id: number, 
  countryCode: string, 
  phone: string,
  client_id: string,
  client_secret: string
) => {
  // Define the request body type with c_hash
  interface CheckPhoneRequestBody {
    marketplace_user_id: number;
    countryCode: string;
    phone: string;
    sendOtp: number;
    c_hash?: string;
  }
  
  // Prepare request body according to API documentation
  const requestBody: CheckPhoneRequestBody = {
    marketplace_user_id,
    countryCode: countryCode, // Keep the + in country code
    phone,
    sendOtp: 1 // Enable OTP sending
  }
  
  // Generate the c_hash for this request
  requestBody.c_hash = await generateCHash(requestBody);
  
  console.log("[CheckPhone] Request payload:", requestBody)
  
  // Enhanced debugging
  console.log("[CheckPhone] API endpoint:", API.ENDPOINTS.CHECK_PHONE);
  console.log("[CheckPhone] API BASE_URL:", API.BASE_URL);
  console.log("[CheckPhone] Expected proxy URL:", `/api/proxy/${API.ENDPOINTS.CHECK_PHONE.replace(/^\//, '')}`);
  
  // Make API call with just the endpoint path to let proxiedFetch handle the proxy routing
  // Adding additional debug option to trace the network request
  
  const response = await proxiedFetch(API.ENDPOINTS.CHECK_PHONE, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // TODO: Replace with actual client credentials
      'client_id': `${client_id}`,
      'client_secret': `${client_secret}`
    },
    body: JSON.stringify(requestBody)
  })
  
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`)
  }
  
  return await response.json()
}

/**
 * Function to call the verify-phone-otp API
 * @param params - All required parameters for OTP verification
 * @param client_id - Client ID for API authentication
 * @param client_secret - Client Secret for API authentication
 * @returns Promise with the API response
 */
const verifyPhoneOtp = async (params: {
  marketplace_user_id: number
  countryCode: string
  phone: string
  otp: string
  refId: string
  c_hash: string // Not used, kept for compatibility
}, client_id: string, client_secret: string) => {
  // Define the request body type
  interface VerifyOtpRequestBody {
    marketplace_user_id: number;
    codeChallenge: string;
    consentType: string;
    countryCode: string;
    otp: string;
    phone: string;
    redirectUrl: string;
    refId: string;
    programTnC: number;
    privacyPolicyAccepted: number;
    loyalCustomer: string;
    codeVerifier: string;
    c_hash?: string;
  }
  
  // Generate code verifier and challenge for PKCE
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  
  console.log("[VerifyOtp] Generated code verifier:", codeVerifier.substring(0, 10) + "...");
  console.log("[VerifyOtp] Generated code challenge:", codeChallenge.substring(0, 10) + "...");
  
  // Prepare request body according to API documentation
  const requestBody: VerifyOtpRequestBody = {
    marketplace_user_id: params.marketplace_user_id,
    codeChallenge,
    consentType: "Explicit",
    countryCode: params.countryCode, // Keep the + in country code
    otp: params.otp,
    phone: params.phone,
    // redirectUrl: "https://order.qmin.co.in",
    redirectUrl : creds.redirectUrl,
    refId: params.refId,
    programTnC: 1,
    privacyPolicyAccepted: 1,
    loyalCustomer: "Y", // Default value, can be updated with actual value
    codeVerifier
  }
  
  // Generate a new c_hash for this request
  requestBody.c_hash = await generateCHash(requestBody);
  
  console.log("[VerifyOtp] Request payload:", requestBody)
  
  console.log("[VerifyOtp] API endpoint:", API.ENDPOINTS.VERIFY_PHONE_OTP);
  
  // Make API call with just the endpoint path to let proxiedFetch handle the proxy routing
  const response = await proxiedFetch(API.ENDPOINTS.VERIFY_PHONE_OTP, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'client_id': client_id,
      'client_secret': client_secret
    },
    body: JSON.stringify(requestBody)
  })
  
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`)
  }
  
  return await response.json()
}

// User form interface
interface UserFormData {
  title: string;
  firstName: string;
  lastName: string;
  gender: string;
  email: string;
  dob: string;
}

// Auth data interface
interface PendingAuthData {
  marketplace_user_id: string;
  access_token: string;
  refresh_token: string;
  customerHash: string;
  c_hash: string;
}

export const LoginModal = ({ isOpen, onClose }: LoginModalProps) => {
  const { signIn } = useAuth()
  const marketplaceStore = useMarketplaceStore()
  const { login } = useUserStore();
  const router = useRouter()
  const { 
    phoneNumber, 
    setPhoneNumber, 
    countryCode, 
    setCountryCode,
    isOtpSent,
    setIsOtpSent,
    otp,
    setOtp,
    // Terms acceptance
    termsAccepted,
    setTermsAccepted,
    // OTP response data
    refId,
    setRefId,
    c_hash,
    setC_hash,
    userType,
    setUserType,
    loyalCustomer,
    setLoyalCustomer,
    passwordSetFlag,
    setPasswordSetFlag,
    phoneNumberEnrolled,
    setPhoneNumberEnrolled,
    // PKCE data
    codeVerifier,
    setCodeVerifier,
    codeChallenge,
    setCodeChallenge
  } = useLoginFormStore()

  // States for user details modal
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [pendingAuthData, setPendingAuthData] = useState<PendingAuthData | null>(null);
  const [userForm, setUserForm] = useState<UserFormData>({
    title: "Mr.",
    firstName: "",
    lastName: "",
    gender: "Male",
    email: "",
    dob: ""
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resendTimeout, setResendTimeout] = useState(60)
  const [canResend, setCanResend] = useState(false)

  // Start countdown timer when OTP is sent
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (isOtpSent && resendTimeout > 0) {
      setCanResend(false)
      timer = setInterval(() => {
        setResendTimeout((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            setCanResend(true)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    
    return () => {
      if (timer) clearInterval(timer)
    }
  }, [isOtpSent, resendTimeout])
  
  const handleSendOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    setError(null)
    setIsSubmitting(true)
    
    if (!phoneNumber || phoneNumber.length < 10) {
      setError("Please enter a valid phone number")
      setIsSubmitting(false)
      return
    }
    
    try {
      // Get marketplace_user_id from the marketplace store/IndexedDB
      const marketplace_id_str = marketplaceStore.getConfigItem("marketplace_user_id", "")
      
      if (!marketplace_id_str) {
        console.error("[Login] Missing marketplace user ID")
        setError("Configuration error. Please try again later.")
        setIsSubmitting(false)
        return
      }
      
      // Convert to number as required by the API
      const marketplace_id = parseInt(marketplace_id_str, 10)
      
      console.log("[Login] Sending OTP to:", { countryCode, phoneNumber, marketplace_id })
      
      const client_id = marketplaceStore.getConfigItem("settings.tata_neu_client_id_web", "");
      const client_secret = marketplaceStore.getConfigItem("settings.tata_neu_client_secret", "");
      // Call the check-phone API 
      const response = await checkPhone(
        marketplace_id, // Pass as number
        countryCode,
        phoneNumber,
        client_id,
        client_secret
      )
      
      console.log("[Login] Check-phone response:", response)
      
      // Store the response data in the store
      if (response.refId) {
        setRefId(response.refId)
        setC_hash(response.c_hash || null)
        setUserType(response.userType || null)
        setLoyalCustomer(response.loyalCustomer || null)
        setPasswordSetFlag(response.passwordSetFlag || null)
        setPhoneNumberEnrolled(response.phoneNumberEnrolled || null)
        
        setIsOtpSent(true)
        setResendTimeout(60)
        setCanResend(false)
      } else {
        setError(response?.message || "Failed to send OTP. Please try again.")
      }
    } catch (error) {
      console.error("[Login] OTP send error:", error)
      setError(error instanceof Error ? error.message : "Failed to send OTP. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }
  
  const handleResendOtp = async () => {
    if (!canResend) return
    setError(null)
    setIsSubmitting(true)
    setOtp("") // Clear OTP field
    
    // Check if we have all required parameters
    if (!refId || !c_hash) {
      console.error("[ResendOtp] Missing refId or c_hash")
      // Fall back to the normal send OTP flow
      await handleSendOtp()
      return
    }
    
    try {
      // Get marketplace_user_id from the marketplace store
      const marketplace_id_str = marketplaceStore.getConfigItem("marketplace_user_id", "")
      
      if (!marketplace_id_str) {
        console.error("[ResendOtp] Missing marketplace user ID")
        setError("Configuration error. Please try again later.")
        setIsSubmitting(false)
        return
      }
      
      // Convert to number as required by the API
      const marketplace_id = parseInt(marketplace_id_str, 10)
      
      console.log("[ResendOtp] Resending OTP with refId:", refId)
      
      // Get client credentials
      const client_id = marketplaceStore.getConfigItem("settings.tata_neu_client_id_web", "");
      const client_secret = marketplaceStore.getConfigItem("settings.tata_neu_client_secret", "");
      
      // Call resend-otp API
      const response = await resendOtp(
        marketplace_id, // Pass as number
        refId,
        c_hash,
        client_id,
        client_secret
      )
      
      console.log("[ResendOtp] Response:", response)
      
      if (response.message) {
        // Update c_hash from response
        if (response.refId) {
          setRefId(response.refId) // Store the new refId
        }
        if (response.c_hash) {
          setC_hash(response.c_hash)
        }
        
        setResendTimeout(60)
        setCanResend(false)
      } else {
        setError("Failed to resend OTP. Please try again.")
      }
    } catch (error) {
      console.error("[ResendOtp] Error:", error)
      setError(error instanceof Error ? error.message : "Failed to resend OTP. Please try again.")
      
      // If resend fails, clear refId and c_hash to force using the normal flow next time
      setRefId(null)
      setC_hash(null)
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Function to call the SSO authenticate API
   * @param params - All required parameters for SSO authentication
   * @returns Promise with the API response
   */
  const authenticateSso = async (params: {
    marketplace_user_id: string;
    access_token: string;
    refresh_token: string;
    customerHash: string;
    c_hash: string;
    title?: string;
    firstName?: string;
    lastName?: string;
    dob?: string;
    gender?: string;
    emailId?: string;
    userType?: string;
  }) => {
    // Define the request body type
    interface SsoAuthRequestBody {
      marketplace_user_id: string;
      external_param?: {
        category?: string;
        utm_source?: string;
        utm_medium?: string;
        jarvisId?: string;
        anonymousId?: string;
        sessionId?: string;
        lat?: string;
        long?: string;
        city?: string;
        pincode?: string;
        access_token?: string;
        source?: string;
        is_consumed?: string;
      };
      access_token: string;
      customerHash?: string;
      title?: string;
      firstName?: string;
      lastName?: string;
      dob?: string;
      gender?: string;
      emailId?: string;
      refresh_token: string;
      userType?: string;
      c_hash: string;
    }
    
    // Prepare request body according to API documentation
    const requestBody: SsoAuthRequestBody = {
      marketplace_user_id: String(params.marketplace_user_id),
      access_token: params.access_token,
      refresh_token: params.refresh_token,
      customerHash: params.customerHash,
      c_hash: params.c_hash,
      // Include optional user details if provided
      ...(params.title && { title: params.title }),
      ...(params.firstName && { firstName: params.firstName }),
      ...(params.lastName && { lastName: params.lastName }),
      ...(params.dob && { dob: params.dob }),
      ...(params.gender && { gender: params.gender }),
      ...(params.emailId && { emailId: params.emailId }),
      ...(params.userType && { userType: params.userType })
    }
    
    console.log("[SSO Auth] Request payload:", requestBody);
    console.log("[SSO Auth] API endpoint:", API.ENDPOINTS.SSO_AUTHENTICATE);
    
    // Make API call using proxiedFetch with just the endpoint path
    // Using the endpoint constant to ensure proper proxy routing
    requestBody.c_hash = await generateCHash(requestBody);
    const response = await proxiedFetch(API.ENDPOINTS.SSO_AUTHENTICATE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Function to call the marketplace vendor login API
   * @param params - Parameters for marketplace vendor login
   * @returns Promise with the API response
   */
  const marketplaceVendorLogin = async (params: {
    access_token: string;
  }) => {
    interface VendorLoginRequestBody {
      tata_login: number;
      marketplace_reference_id: string;
      app_access_token: string;
    }
    
    // Prepare request body
    const requestBody: VendorLoginRequestBody = {
      tata_login: 1, // Required parameter
      marketplace_reference_id: API.MARKETPLACE_REFERENCE_ID,
      app_access_token: params.access_token
    }
    
    console.log("[Vendor Login] Request payload:", requestBody);
    console.log("[Vendor Login] API endpoint:", API.ENDPOINTS.MARKETPLACE_VENDOR_LOGIN);
    
    // Make API call using proxiedFetch with just the endpoint path
    const response = await proxiedFetch(API.ENDPOINTS.MARKETPLACE_VENDOR_LOGIN, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    const responseData = await response.json();
    
    // Store the response in IndexedDB
    try {
      console.log("[Vendor Login] Storing vendor login response in IndexedDB");
      await storageService.setItem(
        `${VENDOR_LOGIN_CACHE_KEY}-${API.MARKETPLACE_REFERENCE_ID}`, 
        responseData
      );
      console.log("[Vendor Login] Successfully stored vendor login data");
    } catch (storageError) {
      console.error("[Vendor Login] Error storing vendor login data:", storageError);
      // Continue even if storage fails - we still have the data in the response
    }
    
    return responseData;
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)
    
    if (isNaN(parseInt(otp))) {
      setError("Please enter a valid OTP")
      setIsSubmitting(false)
      return
    }

    if (!otp || otp.length < 4) {
      setError("Please enter a valid OTP")
      setIsSubmitting(false)
      return
    }
    
    // Check if we have all required parameters
    if (!refId || !c_hash) {
      setError("Missing required verification data. Please request a new OTP.")
      setIsSubmitting(false)
      return
    }
    
    try {
      // Get marketplace_user_id from the marketplace store
      const marketplace_id_str = marketplaceStore.getConfigItem("marketplace_user_id", "")
      
      if (!marketplace_id_str) {
        console.error("[Login] Missing marketplace user ID")
        setError("Configuration error. Please try again later.")
        setIsSubmitting(false)
        return
      }
      
      // Convert to number as required by the API
      const marketplace_id = parseInt(marketplace_id_str, 10)
      
      console.log("[Login] Verifying OTP:", { otp, refId, marketplace_id })
      
      // Get client credentials
      const client_id = marketplaceStore.getConfigItem("settings.tata_neu_client_id_web", "");
      const client_secret = marketplaceStore.getConfigItem("settings.tata_neu_client_secret", "");
      
      // Call verify-phone-otp API
      const response = await verifyPhoneOtp({
        marketplace_user_id: marketplace_id, // Pass as number
        countryCode,
        phone: phoneNumber,
        otp,
        refId,
        c_hash
      }, client_id, client_secret)
      
      console.log("[Login] Verify-otp response:", response)
      
      if (response.accessToken) {
        try {
          // Call the SSO authentication API with the required parameters
          console.log("[Login] Calling SSO authenticate API");

          const ssoResponse = await authenticateSso({
            marketplace_user_id: marketplace_id_str,
            access_token: response.accessToken,
            refresh_token: response.refreshToken,
            customerHash: response.idToken.customerHash,
            c_hash: c_hash
          });
          
          console.log("[Login] SSO authenticate response:", ssoResponse);

          if (Number(ssoResponse?.data?.get_user_detail)) {
            // Store auth data for later use
            setPendingAuthData({
              marketplace_user_id: marketplace_id_str,
              access_token: response.accessToken,
              refresh_token: response.refreshToken,
              customerHash: response.idToken.customerHash,
              c_hash: c_hash
            });
            
            // Show user details modal
            setShowUserDetailModal(true);
            setIsSubmitting(false);
            return;
          }
          // Call marketplace vendor login with access token from the SSO response
          console.log("[Login] Calling marketplace vendor login API");
          const vendorLoginResponse = await marketplaceVendorLogin({
            access_token: ssoResponse.data.access_token
          });

          if (vendorLoginResponse?.status != 200) {
            setError("Failed to verify OTP. Please try again.")
            return
          }

          console.log("[Login] Marketplace vendor login response:", vendorLoginResponse);
          
          // Now call the existing login function with user data
          // Reset form state
          
          setRefId(null)
          setC_hash(null)
        
          // Store token in localStorage to ensure it's available for state management
          try {
            console.log("[Login] Storing access token in localStorage");
            localStorage.setItem('app_access_token', vendorLoginResponse?.data?.app_access_token);
            console.log("[Login] Access token stored successfully");
          } catch (storageError) {
            console.error("[Login] Error storing token:", storageError);
          }
          
          // Close modal and sign in with NextAuth
        
          login(JSON.stringify(vendorLoginResponse));
          // Use the useAuth hook's signIn function
          await signIn(vendorLoginResponse?.data?.app_access_token, JSON.stringify(vendorLoginResponse));
          await storageService.setItem(
            `${VENDOR_LOGIN_CACHE_KEY}-${API.MARKETPLACE_REFERENCE_ID}`, 
            vendorLoginResponse
          );
          onClose();
          setIsOtpSent(false);
          setOtp("");
          setPhoneNumber("");
          // Redirect to business category page after successful login
          window.location.replace('/business-category'); 
          // router.push('/business-category');
        } catch (ssoError) {
          console.error("[Login] SSO authentication error:", ssoError);
          setError(ssoError instanceof Error ? ssoError.message : "Failed to complete authentication. Please try again.");
        }
      } else {
        setError(response.message || "Failed to verify OTP. Please try again.")
      }
    } catch (error) {
      console.error("[Login] OTP verification error:", error)
      setError(error instanceof Error ? error.message : "Failed to verify OTP. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  // Modal backdrop and content animations
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  }
  
  const modalVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", damping: 25, stiffness: 500 } }
  }

  // Handle user details form submission
  const handleUserDetailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    
    try {
      if (!pendingAuthData) {
        throw new Error("Missing authentication data");
      }

      // Call authenticateSso with the stored data plus form data
      const enhancedSsoResponse = await authenticateSso({
        ...pendingAuthData,
        title: userForm.title,
        firstName: userForm.firstName,
        lastName: userForm.lastName,
        gender: userForm.gender,
        emailId: userForm.email,
        dob: userForm.dob,
        userType: 'new'
      });
      
      // Reset modal state
      setShowUserDetailModal(false);
      setPendingAuthData(null);
      
      // Continue with the rest of the existing login flow
      console.log("[Login] Calling marketplace vendor login API");
      const vendorLoginResponse = await marketplaceVendorLogin({
        access_token: enhancedSsoResponse.data.access_token
      });
      
      // All the existing flow
      setRefId(null);
      setC_hash(null);
      
      try {
        console.log("[Login] Storing access token in localStorage");
        localStorage.setItem('app_access_token', vendorLoginResponse?.data?.app_access_token);
        console.log("[Login] Access token stored successfully");
      } catch (storageError) {
        console.error("[Login] Error storing token:", storageError);
      }
      
      login(JSON.stringify(vendorLoginResponse));
      await signIn(vendorLoginResponse?.data?.app_access_token, JSON.stringify(vendorLoginResponse));
      await storageService.setItem(
        `${VENDOR_LOGIN_CACHE_KEY}-${API.MARKETPLACE_REFERENCE_ID}`, 
        vendorLoginResponse
      );
      onClose();
      setIsOtpSent(false);
      setOtp("");
      setPhoneNumber("");
      window.location.href='/business-category'
    } catch (error) {
      console.error("[UserDetail] Error:", error);
      setError(error instanceof Error ? error.message : "Failed to complete registration. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <motion.div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={backdropVariants}
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            className="relative z-10 w-full max-w-md px-4"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={modalVariants}
          >
            <Card className="bg-white/95 backdrop-blur-sm border-none shadow-xl overflow-hidden">
              {/* Close button */}
              <div className="absolute right-4 top-4">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={onClose}
                  className="text-amber-800 hover:text-amber-900 hover:bg-amber-100/80"
                >
                  <X size={20} />
                </Button>
              </div>
              
              <CardContent className="p-8 pt-10">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4 }}
                  className="space-y-6 text-center"
                >
                  <h1 className="text-3xl font-serif text-amber-800">
                    {isOtpSent ? "Verify OTP" : "Welcome"}
                  </h1>
                  <p className="text-amber-700 opacity-80">
                    {isOtpSent 
                      ? `Enter the OTP sent to ${countryCode} ${phoneNumber}`
                      : "To the world of Qmin"
                    }
                  </p>
                  
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-sm bg-red-50 p-2 rounded-md"
                    >
                      {error}
                    </motion.div>
                  )}
                  
                  {!isOtpSent ? (
                    <form onSubmit={handleSendOtp} className="space-y-4 mt-6">
                      <div className="flex items-center">
                        <CustomSelect 
                          value={countryCode} 
                          onValueChange={setCountryCode}
                          options={countryList}
                          className="w-24"
                          triggerClassName="bg-white border-r-0 rounded-r-none rounded-l-lg h-10 border-black-200"
                          placeholder="+91"
                        />
                        
                        <Input 
                          type="tel" 
                          placeholder="Phone Number" 
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          className="flex-1 rounded-l-none rounded-r-lg h-10 border-black-200"
                          required
                        />
                      </div>
                      
                      {/* Terms & Privacy Policy Checkbox */}
                      <div className="flex items-start mt-4">
                        <div className="flex items-center h-5">
                          <input
                            id="terms"
                            type="checkbox"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            className="w-4 h-4 border border-amber-300 rounded bg-amber-50 focus:ring-3 focus:ring-amber-300"
                            required
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="terms" className="text-gray-600 text-left">
                            I agree to the{" "}
                            <a href="https://www.qmin.co.in/privacy-policy" className="text-amber-700 hover:underline">
                              Terms & Conditions
                            </a>{" "}
                            and{" "}
                            <a href="https://www.qmin.co.in/privacy-policy" className="text-amber-700 hover:underline">
                              Privacy Policy
                            </a>
                          </label>
                        </div>
                      </div>
                      
                      <motion.div
                        whileHover={{ scale: termsAccepted ? 1.02 : 1 }}
                        whileTap={{ scale: termsAccepted ? 0.98 : 1 }}
                      >
                        <Button 
                          type="submit" 
                          className="w-full bg-red-700 hover:bg-red-800 text-white py-6"
                          disabled={isSubmitting || !termsAccepted}
                        >
                          {isSubmitting ? "SENDING OTP..." : "SEND OTP"}
                        </Button>
                      </motion.div>
                    </form>
                  ) : (
                    <form onSubmit={handleVerifyOtp} className="space-y-4 mt-6">
                      <Input 
                        type="text" 
                        placeholder="Enter OTP" 
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        className="text-center text-lg tracking-widest"
                        maxLength={6}
                        required
                      />
                      
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Button 
                          type="submit" 
                          className="w-full bg-red-700 hover:bg-red-800 text-white py-6"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "VERIFYING..." : "VERIFY & LOGIN"}
                        </Button>
                      </motion.div>
                      
                      <div className="flex flex-col space-y-2 text-center pt-2">
                        <div className="flex justify-center items-center space-x-4">
                          <Button 
                            variant="link" 
                            onClick={() => {
                              setIsOtpSent(false)
                              setOtp("") // Clear OTP field
                            }}
                            className="text-amber-800"
                          >
                            Change Phone Number
                          </Button>
                          <span className="text-gray-300">|</span>
                          <Button 
                            variant="link" 
                            onClick={handleResendOtp}
                            disabled={!canResend}
                            className={`${canResend ? "text-amber-800" : "text-gray-400"}`}
                          >
                            {canResend ? "Resend OTP" : `Resend in ${resendTimeout}s`}
                          </Button>
                        </div>
                      </div>
                    </form>
                  )}
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}

      {/* User Details Modal */}
      {showUserDetailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <motion.div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          
          {/* Modal */}
          <motion.div
            className="relative z-10 w-full max-w-md px-4"
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 500 }}
          >
            <Card className="bg-white/95 backdrop-blur-sm border-none shadow-xl overflow-hidden">
              <CardContent className="p-8 pt-10">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4 }}
                  className="space-y-6"
                >
                  <div className="text-center">
                    <h1 className="text-3xl font-serif text-amber-800">Welcome</h1>
                    <p className="text-amber-700 opacity-80">To the World of Qmin</p>
                  </div>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-sm bg-red-50 p-2 rounded-md"
                    >
                      {error}
                    </motion.div>
                  )}

                  <form onSubmit={handleUserDetailSubmit} className="space-y-4">
                    {/* Title */}
                    <div className="space-y-2">
                      <label className="text-sm text-gray-600 flex items-center">
                        Title <span className="text-red-500 ml-1">*</span>
                      </label>
                      <select 
                        value={userForm.title}
                        onChange={(e) => setUserForm(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full rounded-lg h-10 border-gray-300 focus:border-red-500 focus:ring-red-500"
                        required
                      >
                        {['Mr.', 'Mrs.', 'Miss.', 'Dr.', 'Ms.', 'Prof.'].map(title => (
                          <option key={title} value={title}>{title}</option>
                        ))}
                      </select>
                    </div>

                    {/* First Name */}
                    <div className="space-y-2">
                      <label className="text-sm text-gray-600 flex items-center">
                        First Name <span className="text-red-500 ml-1">*</span>
                      </label>
                      <Input 
                        type="text"
                        value={userForm.firstName}
                        onChange={(e) => setUserForm(prev => ({ ...prev, firstName: e.target.value }))}
                        className="rounded-lg h-10 border-gray-300 focus:border-red-500 focus:ring-red-500"
                        required
                      />
                    </div>

                    {/* Last Name */}
                    <div className="space-y-2">
                      <label className="text-sm text-gray-600 flex items-center">
                        Last Name <span className="text-red-500 ml-1">*</span>
                      </label>
                      <Input 
                        type="text"
                        value={userForm.lastName}
                        onChange={(e) => setUserForm(prev => ({ ...prev, lastName: e.target.value }))}
                        className="rounded-lg h-10 border-gray-300 focus:border-red-500 focus:ring-red-500"
                        required
                      />
                    </div>

                    {/* Gender */}
                    <div className="space-y-2">
                      <label className="text-sm text-gray-600 flex items-center">
                        Gender <span className="text-red-500 ml-1">*</span>
                      </label>
                      <select 
                        value={userForm.gender}
                        onChange={(e) => setUserForm(prev => ({ ...prev, gender: e.target.value }))}
                        className="w-full rounded-lg h-10 border-gray-300 focus:border-red-500 focus:ring-red-500"
                        required
                      >
                        {['Male', 'Female', 'Transgender', "I'd rather not say"].map(gender => (
                          <option key={gender} value={gender}>{gender}</option>
                        ))}
                      </select>
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <label className="text-sm text-gray-600 flex items-center">
                        Email <span className="text-red-500 ml-1">*</span>
                      </label>
                      <Input 
                        type="email"
                        value={userForm.email}
                        onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                        className="rounded-lg h-10 border-gray-300 focus:border-red-500 focus:ring-red-500"
                        required
                      />
                    </div>

                    {/* Date of Birth */}
                    <div className="space-y-2">
                      <label className="text-sm text-gray-600 flex items-center">
                        Date of Birth <span className="text-red-500 ml-1">*</span>
                      </label>
                      <Input 
                        type="date"
                        value={userForm.dob}
                        onChange={(e) => setUserForm(prev => ({ ...prev, dob: e.target.value }))}
                        className="rounded-lg h-10 border-gray-300 focus:border-red-500 focus:ring-red-500"
                        max={new Date().toISOString().split('T')[0]} // Restrict to dates before today
                        required
                      />
                    </div>

                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="pt-4"
                    >
                      <Button 
                        type="submit" 
                        className="w-full bg-red-700 hover:bg-red-800 text-white py-6"
                        disabled={isSubmitting || !userForm.dob || !userForm.firstName || !userForm.lastName || !userForm.email || !userForm.gender} // Disable if any field is empty
                      >
                        {isSubmitting ? "Processing..." : "Click to proceed"}
                      </Button>
                    </motion.div>
                  </form>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

// Export a provider hook for login modal
export const useLoginModal = () => {
  const [isOpen, setIsOpen] = useState(false)
  
  const openLoginModal = () => setIsOpen(true)
  const closeLoginModal = () => setIsOpen(false)
  
  return {
    isOpen,
    openLoginModal,
    closeLoginModal,
    LoginModal
  }
}
